import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion from "@insite/mobius/Accordion";
import AccordionSection, { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import MinusIcon from "@tSurya/Icons/MinusIcon";
import PlusIcon from "@tSurya/Icons/PlusIcon";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import parse from "html-react-parser";
import React from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    tabTitle = "tabTitle",
    desc = "desc",
    numberOfTabs = "numberOfTabs",
    faqBoxes = "faqBoxes",
    cssClass = "cssClass",
    image = "image",
    newImage = "newImage",
    tabdescription = "tabdescription",
    name = "name",
}

interface NameArray {
    name: string;
    tabdescription: string;
    faqBoxes: FAQModel[];
}

interface TabModel {
    fields: NameArray;
}

interface FAQArray {
    questions: string;
    answers: string;
    newImage: string;
}

interface FAQModel {
    fields: FAQArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabTitle]: string;
        [fields.desc]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.faqBoxes]: FAQModel;
        [fields.cssClass]: string;
        [fields.tabdescription]: string;
    };
}

type Props = OwnProps;
export interface SurTabularDataStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    container?: InjectableCss;
    desc?: InjectableCss;
    imageStyles?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    accordianContainer?: InjectableCss;
    accordianSection?: AccordionSectionPresentationProps;
    tabdesc?: InjectableCss;
}
export const tabularDataStyles: SurTabularDataStyles = {
    tabView: {
        css: css`
            background: ${getColor("common.background")};
            padding: 8px 12px;
            font-style: normal;
            min-width: auto;
            outline: 0;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.msmall};
            color: ${getColor("text.main")};
            cursor: pointer;
            :focus {
                outline: 0;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    css`
                        padding: 12px;
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="true"] {
                color: ${COLORS.selectedButton};
                border-bottom: 2px solid ${getColor("common.border")};
            }
        `,
    },
    tabTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-style: normal;
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-size: ${TYPOGRAPHY.size.xlargest};
            line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
            color: ${getColor("text.main")};
            /* identical to box height, or 117% */
            margin-bottom: 30px;
            text-align: center;
            text-transform: uppercase;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    css`
                        font-weight: ${TYPOGRAPHY.weight.mediumbold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.xlargest};
                        line-height: ${TYPOGRAPHY.lineHeight.xxmedium};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    desc: {
        css: css`
            text-align: center;
            width: 100%;
            margin-bottom: 36px;
            font-family: ${TYPOGRAPHY.fontFamily.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.xbase};
            font-weight: ${TYPOGRAPHY.weight.base};
            color: ${getColor("text.main")};
        `,
    },
    imageStyles: {
        css: css`
            width: 100%;
            max-width: 240px;
            max-height: 240px;
            img {
                height: 100%;
                max-height: 240px;
            }
        `,
    },
    container: {
        css: css`
            background: ${getColor("common.backgroundContrast")} 0.5;
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                gap: 20px;
                z-index: 9;
                /* margin-bottom: 35px;
                margin-top: 60px; */
                margin: 60px auto 35px;
                > li > span {
                    font-family: ${TYPOGRAPHY.fontFamily.title};
                    font-weight: ${TYPOGRAPHY.weight.bold};
                    color: ${getColor("text.main")};
                    font-size: ${TYPOGRAPHY.size.small};
                    line-height: ${TYPOGRAPHY.lineHeight.xbase};
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            css`
                                font-family: ${TYPOGRAPHY.fontFamily.title};
                                font-weight: ${TYPOGRAPHY.weight.base};
                                color: ${getColor("text.main")};
                                font-size: ${TYPOGRAPHY.size.base};
                                line-height: ${TYPOGRAPHY.lineHeight.msmall};
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            tabContent: css`
                border: none;
                padding: 0px !important;
                max-width: 955px;
                margin: 0 auto;
                margin-bottom: 60px;
            `,
        },
    },
    accordianContainer: {
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            &:first-of-type {
                border-top: 1px solid ${getColor("common.border")};
            }
            > dt > button {
                border: 0px !important;
            }
            background: ${getColor("common.backgroundContrast")} !important;
        `,
    },
    accordianSection: {
        headerProps: {
            css: css`
                & button,
                & button:focus {
                    background-color: ${getColor("common.backgroundContrast")};

                    padding: 24px 20px;
                    border: 0;
                    > span:first-of-type {
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.bold};
                        color: ${getColor("text.main")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        panelProps: {
            css: css`
                padding: 9px 20px 25px 40px;
                border: 0;
                &:not(dd[hidden]) {
                    display: flex;
                    display: flex;
                    align-items: center;
                }
                > span {
                    padding-left: 40px;
                    > p {
                        display: list-item;
                        margin-bottom: 0;
                        font-family: ${TYPOGRAPHY.fontFamily.title};
                        font-weight: ${TYPOGRAPHY.weight.base};
                        color: ${getColor("text.accent")};
                        font-size: ${TYPOGRAPHY.size.smaller};
                        line-height: ${TYPOGRAPHY.lineHeight.base};
                    }
                }
            `,
        },
        expandIconProps: {
            src: PlusIcon,
        },
        collapseIconProps: {
            src: MinusIcon,
        },
    },
    tabdesc: {
        css: css`
            text-align: center;
            width: 100%;
            margin-bottom: 36px;
            font-family: "Mulish";
            font-style: normal;
            font-weight: 400;
            font-size: 15px;
            line-height: 24px;
            text-align: center;
            color: ${getColor("text.main")};
        `,
    },
};

const styles = tabularDataStyles;

const SurTabAccordian: React.FC<Props> = ({ fields, id }) => {
    const [expandedAccordian, setExpandedAccordian] = React.useState<number | null>(null);

    const AllContent = [...fields.numberOfTabs];

    return (
        <StyledWrapper {...styles.container}>
            <TabGroup current={AllContent[0]?.fields.name} {...styles.tabGroup}>
                {AllContent.map(numberOfTab => {
                    return (
                        // eslint-disable-next-line react/jsx-key
                        <Tab
                            headline={numberOfTab.fields.name}
                            tabKey={numberOfTab.fields.name}
                            {...styles.tabView}
                            onClickCapture={() => setExpandedAccordian(null)}
                        >
                            <Typography as="h2" {...styles.tabTitle}>
                                {numberOfTab.fields.name}
                            </Typography>
                            <Typography as="p" {...styles.tabdesc}>
                                {numberOfTab.fields.tabdescription}
                            </Typography>
                            <Accordion headingLevel={2}>
                                {numberOfTab.fields.faqBoxes.map((faqs: FAQModel, index: number) => {
                                    return (
                                        // eslint-disable-next-line react/no-array-index-key, react/jsx-key
                                        <StyledWrapper {...tabularDataStyles.accordianContainer} key={index}>
                                            <AccordionSection
                                                {...tabularDataStyles.accordianSection}
                                                title={faqs.fields.questions}
                                                expanded={index === expandedAccordian}
                                                onTogglePanel={() =>
                                                    index === expandedAccordian
                                                        ? setExpandedAccordian(null)
                                                        : setExpandedAccordian(index)
                                                }
                                            >
                                                <LazyImage src={faqs.fields.newImage} {...styles.imageStyles} />
                                                <Typography>{parse(faqs.fields.answers, parserOptions)}</Typography>
                                            </AccordionSection>
                                        </StyledWrapper>
                                    );
                                })}
                            </Accordion>
                        </Tab>
                    );
                })}
            </TabGroup>
        </StyledWrapper>
    );
};

const SurTabImageAccordianWidget: WidgetModule = {
    component: SurTabAccordian,
    definition: {
        group: "Common",
        displayName: "Sur Tab Image Accordian Widget",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.desc,
                displayName: "Descriptioon",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.numberOfTabs,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Tab Name",
                        editorTemplate: "TextField",
                        sortOrder: 0,
                        defaultValue: "",
                    },
                    {
                        name: "tabdescription",
                        displayName: "Tab Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },

                    {
                        name: fields.faqBoxes,
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields) => {
                            return item.fields.questions;
                        },
                        defaultValue: [],
                        sortOrder: 1,
                        fieldDefinitions: [
                            {
                                name: "questions",
                                displayName: "Title",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                displayName: "Image",
                                name: "newImage",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                            },
                            {
                                name: "answers",
                                displayName: "Description",
                                editorTemplate: "RichTextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default SurTabImageAccordianWidget;
